import { alpha } from "@mui/material/styles";

/**
 * How strongly a colour tints a surface: its opacity over white. Every tinted background and
 * outline in the app takes one of these levels through `tint()`, so their contrast is set here
 * for the whole app. MUI's own hover and selection (grid rows, menus) use the same levels
 * (`palette.action`).
 */
export const TINT = {
  surface: 0.1, // a card header, a tab bar, an info box, a changed field
  hover: 0.1, // under the pointer, added to the level of what it tints
  selected: 0.2, // a selected or marked row, the chosen entry, the track of a bar
  badge: 0.3, // pills, icon squares, avatars
  border: 0.5, // a tinted outline
};

/**
 * A palette tone as a tinted background or outline. Tones tint from their `light` shade, the vivid
 * one, so a warning stays yellow; text and icons take `main` and darker.
 *
 * @param {object} theme
 * @param {string} tone - palette key ("primary", "warning", …); anything else tints grey
 * @param {...(string|false)} levels - TINT levels, added up; a falsy one is left out, so a state can
 *   switch one on: `tint(theme, "primary", isSelected && "selected", "hover")`
 * @returns {string} an rgba() colour
 */
export const tint = (theme, tone, ...levels) => {
  const opacity = levels.filter(Boolean).reduce((sum, level) => {
    if (!(level in TINT)) throw new Error(`Unknown tint level "${level}"`);
    return sum + TINT[level];
  }, 0);
  return alpha(theme.palette[tone]?.light ?? theme.palette.grey[500], opacity);
};
